import type { AutoSuggestionCustomer } from "~/features/assistedService/types";
import { getAsmAccessToken, readStoredAsmToken, refreshAsmAccessToken } from "~/features/assistedService/api/asmAuth";

/**
 * ASWS (`/assistedservicewebservices`) adapters for the ASM bar — task 0250
 * lane 2a. Every call carries the agent JWT as Bearer (ASWS is auth-gated:
 * JWT + `ROLE_ASAGENTGROUP`, see the 0246 inventory). Paths are
 * root-relative: ASWS is a sibling webapp context outside the storefront
 * language contextPath.
 */
const ASWS_BASE = "/assistedservicewebservices";

function requireBaseSiteId(): string {
  const siteUid = window.__SCHA_BOOT__?.siteUid;
  if (!siteUid) {
    throw new Error("Cannot build ASWS URL without __SCHA_BOOT__.siteUid");
  }
  return siteUid;
}

/**
 * One retry shape shared by the ASWS adapters: an expired access token gets
 * a silent refresh (rotating RT) and the call is retried once; a second 401
 * surfaces as an error to the caller (the bar's alert lane).
 */
async function authedAswFetch(path: string, init: RequestInit): Promise<Response> {
  const token = readStoredAsmToken() ?? (await refreshAsmAccessToken());
  const send = (accessToken: string | null) =>
    fetch(`${ASWS_BASE}${path}`, {
      ...init,
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      },
    });

  const response = await send(token?.token ?? getAsmAccessToken());
  if (response.status !== 401) {
    return response;
  }
  const renewed = await refreshAsmAccessToken();
  return send(renewed?.token ?? null);
}

export interface AswsErrorShape {
  message: string;
  status: number;
}

/** Normalizes an ASWS error body (`{ errors: [{ message, type }] }`) for the bar's alert lane. */
export async function extractAswsError(response: Response): Promise<AswsErrorShape> {
  const body = (await response.json().catch(() => null)) as { errors?: Array<{ message?: string }> } | null;
  const message = body?.errors?.[0]?.message ?? `asm.error (${response.status})`;
  return { message, status: response.status };
}

/**
 * Customer autocomplete via the Schaeffler ASWS route
 * `POST /customers/suggestions/schaeffler-search` (replaces the legacy storefront
 * `GET /assisted-service/autocomplete`; the ASWS `GET /customers/autocomplete`
 * twin is deprecated-for-removal and must never be used). Request body is
 * `CustomerSuggestionsInputWsDTO` (`{ customerQuery }`); the response is the
 * unchanged `AutoSuggestionCustomerData` array used by the release/6.6 route.
 */
export async function searchCustomerSuggestions(
  customerQuery: string,
  signal?: AbortSignal,
): Promise<AutoSuggestionCustomer[]> {
  const baseSiteId = requireBaseSiteId();
  const response = await authedAswFetch(
    `/customers/suggestions/schaeffler-search?baseSite=${encodeURIComponent(baseSiteId)}`,
    {
      method: "POST",
      body: JSON.stringify({ customerQuery }),
      signal,
    },
  );
  if (!response.ok) {
    throw await extractAswsError(response);
  }
  return (await response.json()) as AutoSuggestionCustomer[];
}

/**
 * Audit trail START for an emulated session: ASWS
 * `POST /{baseSiteId}/users/{userId}/asmSessionEvents` with
 * `{ eventType: "StartSession" }` (the platform controller accepts START
 * only). Called when the agent personifies a customer.
 */
export async function postAsmSessionEventStart(userId: string): Promise<void> {
  const baseSiteId = requireBaseSiteId();
  const response = await authedAswFetch(
    `/${encodeURIComponent(baseSiteId)}/users/${encodeURIComponent(userId)}/asmSessionEvents`,
    { method: "POST", body: JSON.stringify({ eventType: "StartSession" }) },
  );
  if (!response.ok) {
    throw await extractAswsError(response);
  }
}

/**
 * Audit trail END for an emulated session: the Schaeffler ASWS END
 * counterpart `POST /{baseSiteId}/users/{userId}/asmSessionEvents/end`
 * (task 0251, `ASMSessionEndEventController` — the platform controller
 * accepts START only). Called when the agent stops personifying a customer.
 */
export async function postAsmSessionEventEnd(userId: string): Promise<void> {
  const baseSiteId = requireBaseSiteId();
  const response = await authedAswFetch(
    `/${encodeURIComponent(baseSiteId)}/users/${encodeURIComponent(userId)}/asmSessionEvents/end`,
    { method: "POST" },
  );
  if (!response.ok) {
    throw await extractAswsError(response);
  }
}
